import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { createUseStyles } from "$utils/createUseStyles";

import { LinkOffOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { theme } from "$utils/theme/bataillog";

import { useActivitiesQuery } from "$hooks/useActivitiesQuery";
import useJStyles from "$hooks/useJStyles";
import useCarrierServiceItem from "$hooks/useCarrierServiceItem";

import { Activity, CompanyType } from "$types";

import { Text, Button, Popin, Modal } from "$components";
import GenericSelect from "$forms/GenericSelect";
import CField, { CFieldInputProps } from "$forms/CField";
import CompanySelect from "$forms/CompanySelect";
import { ActivityOption, getActivitiesOptions } from "$utils/typeConstants";
import { CompanyServices } from "$services";
import { CacheInvalidationAction } from "$utils/cache";

type FormValues = {
  companyId: string;
  carrierActivity: Activity["name"] | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  carrierServiceId?: string;
};

function ShippingConfigPopin({
  isOpen,
  onClose,
  onSubmit,
  carrierServiceId,
}: Props) {
  const { jclasses } = useJStyles();
  const classes = useStyles();
  const { data: activities = [] } = useActivitiesQuery();
  const [isDetachModalOpen, toggleDetachModal] = useState<boolean>(false);

  const { carrierService, attachCompany, detachCompany, isFetching } =
    useCarrierServiceItem(carrierServiceId);

  const defaultValues: FormValues = useMemo(
    () => ({
      companyId: carrierService?.companyId,
      carrierActivity: carrierService?.carrierActivity || null,
    }),
    [carrierService]
  );
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
  });
  const companyId = watch("companyId");

  const isDetachButtonDisabled = !carrierService?.companyId;

  const { data: selectedAgency } = useQuery({
    queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, companyId],
    queryFn: () => CompanyServices.getCompanyById(companyId),
    enabled: !!companyId,
    staleTime: 60 * 1000,
  });

  const filteredCarrierActivitiesOptions = useMemo(
    () =>
      getActivitiesOptions(
        activities?.filter((a) =>
          selectedAgency?.activities.some((a2) => a2 === a._id)
        )
      ),
    [activities, selectedAgency?.activities]
  );

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onFormSubmit = useCallback(
    (formValues: FormValues) => {
      attachCompany(carrierService?.id, formValues);
      onSubmit();
    },
    [attachCompany, onSubmit, carrierService]
  );

  const onDetachCompany = async () => {
    toggleDetachModal(false);
    detachCompany(carrierService?.id);
    onSubmit();
  };

  return (
    <Popin openPopin={isOpen} toggleDrawer={onClose}>
      <div className={jclasses.jWrapper}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Text type="h2">Rattacher une agence</Text>
          <Button
            color="primary"
            disabled={isDetachButtonDisabled}
            onClick={() => toggleDetachModal(true)}
            style={{ height: "fit-content" }}
          >
            <LinkOffOutlined />
            {`\xa0Détacher l'agence`}
          </Button>
        </Box>
        <Text type="h3" style={{ margin: 0 }}>
          <b>Service sélectionné : </b> {carrierService?.carrierName}{" "}
          {carrierService?.label} ({carrierService?.serviceName})
        </Text>
        <div className={classes.form}>
          <div>
            <CField
              control={control}
              required
              name="companyId"
              error={errors.companyId}
            >
              {(props: CFieldInputProps) => (
                <CompanySelect
                  {...props}
                  placeholder=""
                  label="Sélectionner une agence existante"
                  companyTypes={[CompanyType.AGENCY]}
                  minCharsToTrigerSearch={0}
                  rowsPerPage={99999}
                />
              )}
            </CField>
          </div>
          <div>
            <CField
              required
              control={control}
              name="carrierActivity"
              error={errors.carrierActivity}
            >
              {(props: CFieldInputProps) => (
                <GenericSelect
                  {...props}
                  value={props.value}
                  getOptionLabel={(option: ActivityOption) => option.label}
                  label="Sélectionner un type de service"
                  options={filteredCarrierActivitiesOptions}
                  onChange={(data: any, fullData: ActivityOption) => {
                    props.onChange(fullData.name);
                  }}
                  valueKey="name"
                />
              )}
            </CField>
          </div>
          <div className={classes.buttonsContainer}>
            <Button color="secondary" onClick={onClose}>
              Retour
            </Button>
            <Button disabled={isFetching} onClick={handleSubmit(onFormSubmit)}>
              Valider
            </Button>
          </div>
        </div>
      </div>
      <Modal
        title="Détachement de l'agence du service"
        textValidate="Oui"
        textCancel="Non"
        open={isDetachModalOpen}
        onValidate={onDetachCompany}
        onCancel={() => toggleDetachModal(false)}
      >
        <div className={jclasses.dialogContent}>
          <Text>
            {`Vous êtes sur le point de détacher l'agence du service sélectionné.`}
            <br />
            Êtes-vous sûr de vouloir continuer ?
          </Text>
        </div>
      </Modal>
    </Popin>
  );
}

export default ShippingConfigPopin;

const useStyles = createUseStyles(() => ({
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
    marginTop: theme.spacing(4),
  },
}));
